/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  ImageUrlGenerator,
  ShowflixFeedItemType
} from "@tivocorp/data-domain-lib";
import type {
  ShowflixCarousel,
  TShowflixCarouselCarouselGetParams
} from "@tivocorp/data-domain-lib";

import { CarouselTemplate, OfferType } from "../../model/enums";
import { PostSigninInteractor } from "../../model/interactor/postSigninInteractor";
import type { ApplicationFeatureArea } from "../../model/enums";
import type {
  TCarouselItem,
  TContentType,
  TNetworkOptions
} from "../../model/types";
import { getTilePixelDimensions } from "./CarouselTemplateUtil";
import { getImageHostBaseUrl } from "./smartCarouselUtility";

const imageUrlGenerator: ImageUrlGenerator = new ImageUrlGenerator();

/** The returned `TContentType` selects which fields a tile's preview renders. */
const mapShowflixContentType = (
  feedItemType: ShowflixFeedItemType
): TContentType => {
  switch (feedItemType) {
    case ShowflixFeedItemType.Episode:
      return "episode";
    case ShowflixFeedItemType.JumpChannel:
    case ShowflixFeedItemType.LinearProvider:
      return "channel";
    case ShowflixFeedItemType.Movie:
      return "movie";
    case ShowflixFeedItemType.Person:
      return "person";
    case ShowflixFeedItemType.Series:
      return "series";
    case ShowflixFeedItemType.SportsEvent:
      return "gameEvent";
    case ShowflixFeedItemType.SportsOrganization:
      return "leagueOrganization";
    case ShowflixFeedItemType.SportsTeam:
      return "team";
    case ShowflixFeedItemType.StreamingProvider:
      return "ottApplication";
    // Promotion has no TContentType equivalent, and the service can add feed
    // item types before this generated client is regenerated.
    default:
      return "series";
  }
};

/** Maps a Showflix carousel's `template` string to the app's `CarouselTemplate`. */
export const mapShowflixCarouselTemplate = (
  template: string | undefined
): CarouselTemplate => {
  switch (template) {
    case CarouselTemplate.LANDSCAPE_HORIZONTAL:
    case CarouselTemplate.LANDSCAPE_GRID:
    case CarouselTemplate.PIP_LANDSCAPE_HORIZONTAL:
    case CarouselTemplate.PIP_LANDSCAPE_GRID:
    case CarouselTemplate.POSTER_HORIZONTAL:
    case CarouselTemplate.LOGO_HORIZONTAL:
      return template;
    default:
      return CarouselTemplate.POSTER_HORIZONTAL;
  }
};

const buildShowflixImageUrl = (
  imageHostBaseUrl: string,
  item: {
    digItemId?: string;
    altFeedItemId?: string;
    feedItemType: ShowflixFeedItemType;
  },
  template: CarouselTemplate
): string => {
  // altFeedItemId is the collection/series image id; digItemId is the fallback.
  const imageId: string = item.altFeedItemId || item.digItemId || "";
  if (imageHostBaseUrl.length === 0 || imageId.length === 0) {
    return "";
  }

  const tileDimensions = getTilePixelDimensions(template);
  return imageUrlGenerator.buildImageUrl(
    imageHostBaseUrl,
    imageId,
    item.feedItemType === ShowflixFeedItemType.Movie ? "MOVIE" : "COLLECTION",
    tileDimensions.width,
    tileDimensions.height
  );
};

/** One page of items mapped from a single Showflix `Carousel` response. */
export interface TShowflixCarouselItemsPage {
  items: TCarouselItem[];
  /** Total items the server reports for this carousel; undefined when omitted. */
  resolvedCount: number | undefined;
  template: CarouselTemplate;
}

/**
 * Maps a single Showflix `Carousel`'s feed items into app-ready tiles.
 *
 * `resolvedCount` is passed through exactly as the server sent it, including
 * `undefined`. Substituting the current item count for a missing total would
 * make an unfinished collection look already complete, and a load-more
 * request for it would never be sent again.
 */
export const mapShowflixCarouselItemsToAppData = (
  carousel: ShowflixCarousel,
  imageHostBaseUrl: string
): TShowflixCarouselItemsPage => {
  const carouselFeedItems = carousel.feedItems;
  const feedItems =
    carouselFeedItems === null ||
    carouselFeedItems === undefined ||
    carouselFeedItems.feedItems === null ||
    carouselFeedItems.feedItems === undefined
      ? []
      : carouselFeedItems.feedItems;
  const template = mapShowflixCarouselTemplate(carousel.template);
  return {
    template,
    resolvedCount:
      carouselFeedItems === null || carouselFeedItems === undefined
        ? undefined
        : carouselFeedItems.resolvedCount,
    items: feedItems.map((item) => ({
      id: item.feedItemId,
      title: item.title,
      description: item.caption || "",
      imageUrl: buildShowflixImageUrl(imageHostBaseUrl, item, template),
      contentType: mapShowflixContentType(item.feedItemType),
      categoryLabel: item.caption || "",
      duration: item.durationSecs || 0,
      isLive: item.isLive || false,
      isUpcoming: item.isUpcoming,
      isNew: item.isNew,
      isAdult: item.isAdult || false,
      feedItemType: item.feedItemType,
      offerId: item.offerId,
      offerType: item.offerType as OfferType | undefined,
      stationId: item.stationId,
      startTime: item.startTime,
      endTime: item.endTime,
      seasonNumber: item.seasonNum,
      episodeNumber: item.episodeNum,
      episodeTitle: item.eventTitle,
      progressPercent: item.percentWatched,
      movieYear: item.movieYear,
      ratingBadge: item.tivoTvRating || ""
    }))
  };
};

/**
 * Fetches one offset-paginated page of items for a single Showflix carousel.
 *
 * Shared by every screen that pages the Showflix `GET /carousels/{name}`
 * endpoint — Shows & Movies' own rows/grid and See More's follow-up pages —
 * so a generic feature (See More) never has to import a specific feature's
 * slice to reach it.
 */
export const fetchShowflixCarouselTilePage = async (
  carouselId: string,
  offset: number,
  count: number,
  genre: string[],
  featureArea?: ApplicationFeatureArea,
  options?: TNetworkOptions
): Promise<TShowflixCarouselItemsPage> => {
  const params: TShowflixCarouselCarouselGetParams = {
    carouselName: carouselId,
    offset,
    count,
    genre: genre.length === 0 ? undefined : genre
  };
  const interactor = new PostSigninInteractor();
  const imageHostBaseUrl = await getImageHostBaseUrl(interactor);
  const response = await interactor.fetchShowflixCarousel(
    params,
    options,
    featureArea
  );
  return mapShowflixCarouselItemsToAppData(response, imageHostBaseUrl);
};
